import type { CSSProperties, ReactNode } from "react";

import { num } from "../../lib/format";
import { useCountUp } from "../../lib/useCountUp";

/** The card: translucent white over the ground, hairline border, 16px radius. */
export function Panel({
  title,
  subtitle,
  action,
  children,
  padding = 22,
  className = "",
  id,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
  padding?: number | string;
  className?: string;
  id?: string;
}) {
  return (
    <section id={id} className={`scroll-mt-6 rounded-lg border border-line bg-glass ${className}`} style={{ padding }}>
      {(title || action) && (
        <header className="mb-[18px] flex items-start justify-between gap-3">
          <div>
            {title && <h2 className="text-[15px] font-semibold tracking-[-0.01em] text-ink">{title}</h2>}
            {subtitle && <p className="mt-0.5 text-xs text-muted">{subtitle}</p>}
          </div>
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

/** One number, one label. */
export function StatTile({ label, value, delta, hint, accent = false }: { label: string; value: number | ReactNode; delta?: number; hint?: string; accent?: boolean }) {
  // A number counts up on its first paint; a node (a formatted string) is shown as is.
  const counted = useCountUp(typeof value === "number" ? value : 0);
  return (
    <div className="rounded-lg border border-line bg-glass px-5 py-[18px]">
      <p className="text-xs font-medium text-muted">{label}</p>
      <div className="mt-2.5 flex items-baseline gap-2.5">
        <p className={`tabular text-[26px] font-semibold leading-none tracking-[-0.03em] ${accent ? "text-accent-2" : "text-ink"}`}>
          {typeof value === "number" ? num(counted) : value}
        </p>
        {delta !== undefined && (
          <span className={`tabular rounded-full px-[7px] py-0.5 text-xs font-semibold ${delta >= 0 ? "bg-ok-soft text-ok" : "bg-danger-soft text-danger"}`}>
            {delta >= 0 ? "+" : ""}
            {delta}%
          </span>
        )}
      </div>
      {hint && <p className="mt-2 text-xs text-faint">{hint}</p>}
    </div>
  );
}

/** Segmented control: glass track, raised active segment. */
export function Tabs<T extends string>({ items, value, onChange, className = "" }: { items: { value: T; label: string }[]; value: T; onChange: (v: T) => void; className?: string }) {
  return (
    <div role="tablist" className={`inline-flex gap-0.5 rounded-md border border-line bg-glass p-[3px] ${className}`}>
      {items.map((it) => {
        const active = it.value === value;
        return (
          <button
            key={it.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(it.value)}
            className={`min-h-[30px] rounded-sm px-3.5 text-[13px] font-medium transition duration-150 focus-visible:outline-none focus-visible:shadow-glow ${
              active ? "bg-glass-2 text-ink shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]" : "text-muted hover:text-ink"
            }`}
          >
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

/** Grey block standing in for content still on its way. Size it with classes. */
export function Skeleton({ className = "" }: { className?: string }) {
  return <span aria-hidden="true" className={`rp-skeleton block ${className}`} />;
}

/** Ghost of a Panel: title line, subtitle line, a few rows. */
export function PanelSkeleton({ rows = 3, className = "" }: { rows?: number; className?: string }) {
  return (
    <div className={`rounded-lg border border-line bg-glass p-[22px] ${className}`}>
      <Skeleton className="h-4 w-36" />
      <Skeleton className="mt-2 h-3 w-56 max-w-full" />
      <div className="mt-5 grid gap-3">
        {Array.from({ length: rows }, (_, i) => (
          <Skeleton key={i} className="h-9" />
        ))}
      </div>
    </div>
  );
}

/** Ghost of a stat tile row. */
export function TilesSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="rounded-lg border border-line bg-glass px-5 py-[18px]">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="mt-3.5 h-7 w-14" />
          <Skeleton className="mt-3 h-3 w-28 max-w-full" />
        </div>
      ))}
    </div>
  );
}

/**
 * Ghost of a list: table rows on desktop, cards on phones. Keeps the panel the
 * same height as the real thing, so nothing jumps when the data lands.
 */
export function ListSkeleton({ rows = 6, mobile = false, className = "" }: { rows?: number; mobile?: boolean; className?: string }) {
  if (mobile) {
    return (
      <div className={`grid gap-2.5 ${className}`}>
        {Array.from({ length: Math.min(rows, 4) }, (_, i) => (
          <div key={i} className="grid gap-3 rounded-[12px] border border-line bg-glass p-3.5">
            <div className="flex justify-between">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-14 rounded-full" />
            </div>
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-32" />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className={`grid gap-px ${className}`}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="grid grid-cols-[90px_80px_1fr_110px_60px_140px_120px] items-center gap-3.5 px-3.5 py-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-14 rounded-full" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-20 rounded-full" />
          <Skeleton className="ml-auto h-4 w-8" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="ml-auto h-7 w-28" />
        </div>
      ))}
    </div>
  );
}

/**
 * Whole-screen ghost: page title, optional tile row, then panels. Shown while
 * a route chunk or a screen's first data is on its way; it fades in after
 * 120 ms so a fast answer skips it entirely.
 */
export function PageSkeleton({ tiles = 0, panels = 2, rows = 3 }: { tiles?: number; panels?: number; rows?: number }) {
  return (
    <div role="status" aria-label="Ładowanie" className="rp-skeleton-page grid gap-5">
      <div>
        <Skeleton className="h-7 w-40" />
        <Skeleton className="mt-2.5 h-3.5 w-64 max-w-full" />
      </div>
      {tiles > 0 && <TilesSkeleton count={tiles} />}
      {Array.from({ length: panels }, (_, i) => (
        <PanelSkeleton key={i} rows={rows} />
      ))}
    </div>
  );
}

/** Empty / loading / error share one quiet block; error gets the red hairline and a retry. */
export function EmptyState({
  variant = "empty",
  title,
  hint,
  action,
  className = "",
}: {
  variant?: "empty" | "loading" | "error";
  title?: string;
  hint?: string;
  action?: ReactNode;
  className?: string;
}) {
  const err = variant === "error";
  return (
    <div
      role={err ? "alert" : "status"}
      className={`grid justify-items-center gap-1.5 rounded-lg px-6 py-11 text-center ${
        err ? "border border-danger/35 bg-danger-soft" : "border border-dashed border-line-strong"
      } ${variant === "loading" ? "rp-skeleton-page" : ""} ${className}`}
    >
      {variant === "loading" && (
        <span aria-label="Ładowanie" className="mb-1.5 h-[22px] w-[22px] rounded-full border-2 border-line-strong border-t-accent-2 [animation:rp-spin_0.8s_linear_infinite]" />
      )}
      {title && <p className={`text-[15px] font-semibold ${err ? "text-danger" : "text-ink"}`}>{title}</p>}
      {hint && <p className="max-w-[360px] text-[13px] text-muted">{hint}</p>}
      {action && <div className="mt-2.5">{action}</div>}
    </div>
  );
}

/** Page title row: 24/700 title, muted subtitle, right-aligned actions. */
export function PageHeader({ title, subtitle, actions, className = "" }: { title: string; subtitle?: ReactNode; actions?: ReactNode; className?: string }) {
  return (
    <header className={`flex flex-col gap-3.5 md:flex-row md:items-end md:justify-between ${className}`}>
      <div>
        <h1 className="text-2xl font-bold tracking-[-0.03em]">{title}</h1>
        {subtitle && <p className="mt-1 text-[13px] text-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
    </header>
  );
}

export function Mono({ children, className = "", style }: { children: ReactNode; className?: string; style?: CSSProperties }) {
  return (
    <code className={`font-mono tracking-[0.02em] ${className}`} style={style}>
      {children}
    </code>
  );
}

/** Label-over-value pair for detail grids. */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="mb-[3px] text-xs font-medium text-muted">{label}</p>
      <div className="tabular text-sm text-ink">{children}</div>
    </div>
  );
}
